require('./RAFrontTestPosition.scss');

// @vendors
const React = require('react');
const PropTypes = require('prop-types');
const classnames = require('classnames');

// @commons
const { OKLink } = require('commonsComponents/OKLink');
const { OKButton } = require('components/commons/OKButton/OKButton');

// @utilities
const { FormattedText } = require('core/i18n').i18n;
const {
    recordLink,
    PROC_RA_GLOBAL_POSITION_INTERACTION,
} = require('utilities/tagging');

// @constants
const {
    SUITABILITY_TEST_STATUS_URL,
    RA_SUITABILITY_TEST_WIDGET,
    PROC_RA_SUITABILITY_TEST_BEGIN_TEST,
    PROC_RA_SUITABILITY_TEST_CHECK_STATUS,
} = require('../../raConstants/index');

const {
    SUITABILITY_TEST_MAINTENANCE_POPUP,
} = require('constants/flagBasedFeatures');

const {
    isNoValidTest,
    isTestValid,
} = require('../../utilities/RASuitabilityTestHelper');

class RAFrontTestPosition extends React.Component {
    handleRecordLink() {
        recordLink(
            PROC_RA_GLOBAL_POSITION_INTERACTION,
            PROC_RA_SUITABILITY_TEST_CHECK_STATUS,
        );
    }

    buildHeader(testStatus, investProfile) {
        const { isValid, isNoValid, isNoFinished, isUnsuitable } = testStatus;
        let header;
        const iconClassName = classnames('ra-front-test-position__icon', {
            ['ra-front-test-position__icon--signed-test']:
                isValid && !isUnsuitable && !isNoFinished,
            ['ra-front-test-position__icon--no-test']:
                (isNoValid && !isValid && !isNoFinished) || isUnsuitable,
            ['ra-front-test-position__icon--unsigned-test']: isNoFinished,
        });
        if (isValid && !isUnsuitable && !isNoFinished) {
            header = (
                <header className="ra-front-test-position__header ra-front-test-position__header--signed-test">
                    <div className="ra-front-test-position__title-container">
                        <FormattedText value="raFrontTestPosition-raFrontTestPosition_completeTestTitle" />
                        <div className="ra-front-test-position__link-container">
                            <OKLink
                                to={SUITABILITY_TEST_STATUS_URL}
                                onClick={this.handleRecordLink}
                                modifier="complementary"
                            >
                                <FormattedText value="raFrontTestPosition-completeTestCTA" />
                            </OKLink>
                        </div>
                    </div>
                    <div className="ra-front-test-position__icon-container ra-front-test-position__icon-container--signed-test">
                        <i className={iconClassName} />
                        <div className="ra-front-test-position__profile">
                            {investProfile}
                        </div>
                    </div>
                </header>
            );
        } else if (isNoValid || isNoFinished) {
            const textValue = isNoFinished
                ? 'raFrontTestPosition-inProgressTestTitle'
                : 'raFrontTestPosition-noTestTitle';
            header = (
                <header className="ra-front-test-position__header">
                    <div className="ra-front-test-position__icon-container">
                        <i className={iconClassName} />
                    </div>
                    <div className="ra-front-test-position__header-title">
                        <FormattedText value={textValue} />
                    </div>
                </header>
            );
        }
        return header;
    }

    recordLinkFrontTestPosition() {
        const { immCheckStatus, validateResidential } = this.props;
        if (immCheckStatus.get('newRoboCustomer')) {
            recordLink(
                PROC_RA_GLOBAL_POSITION_INTERACTION,
                PROC_RA_SUITABILITY_TEST_BEGIN_TEST,
            );
        }
        validateResidential(RA_SUITABILITY_TEST_WIDGET);
    }

    buildContent(testStatus) {
        const { isValid, isNoValid, isNoFinished, isUnsuitable } = testStatus;
        const infoClassName = classnames('ra-front-test-position__info', {
            ['ra-front-test-position__info--light']:
                !isValid || isUnsuitable || (isValid && isNoFinished),
        });
        let info;
        if (isValid && !isUnsuitable && !isNoFinished) {
            info = '';
        } else if ((isNoValid || isUnsuitable) && !isNoFinished) {
            info = <FormattedText value="raFrontTestPosition-noTestInfo" />;
        } else if (isNoFinished) {
            info = (
                <FormattedText value="raFrontTestPosition-inProgressTestInfo" />
            );
        }
        return (
            <div className="ra-front-test-position__content">
                <p className={infoClassName}>{info}</p>
            </div>
        );
    }

    handleGoToTest(testStatus) {
        const { onReturnTestClick } = this.props;
        const { isUnsuitable } = testStatus;
        if (isUnsuitable) {
            recordLink(
                PROC_RA_GLOBAL_POSITION_INTERACTION,
                PROC_RA_SUITABILITY_TEST_BEGIN_TEST,
            );
            onReturnTestClick();
        } else {
            this.recordLinkFrontTestPosition(RA_SUITABILITY_TEST_WIDGET);
        }
    }

    buildLinkButton(testStatus) {
        const { immFlagBasedFeatures, immRAGlobalPosition } = this.props;
        const isTestMaintenance = immFlagBasedFeatures.get(
            SUITABILITY_TEST_MAINTENANCE_POPUP,
        );
        const hasRobo = immRAGlobalPosition.get('hasRoboHired');
        const { isValid, isNoValid, isNoFinished, isUnsuitable } = testStatus;
        let linkButton;
        if (isValid && !isUnsuitable && !isNoFinished) {
            linkButton = null;
        } else if ((isNoValid || isUnsuitable) && !isNoFinished) {
            linkButton = (
                <OKButton
                    modifier="secondary"
                    inverse={true}
                    onClick={() => this.handleGoToTest(testStatus)}
                >
                    <FormattedText value="raFrontTestPosition-noTestCTA" />
                </OKButton>
            );
        } else if (isNoFinished) {
            if (isTestMaintenance && hasRobo) {
                linkButton = null;
            } else {
                linkButton = (
                    <OKButton
                        modifier="secondary"
                        inverse={true}
                        rightIcon="icon-flecha-derecha"
                        onClick={this.props.onReturnTestClick}
                    >
                        <FormattedText value="raFrontTestPosition-inProgressTestCTA" />
                    </OKButton>
                );
            }
        }
        return linkButton;
    }

    render() {
        const { immCheckStatus, investProfile } = this.props;
        const testStatus = {
            isValid: isTestValid(immCheckStatus),
            isNoValid: isNoValidTest(immCheckStatus),
            isUnsuitable: immCheckStatus.get('nonSuitableTest'),
            isNoFinished:
                immCheckStatus.get('testInProgress') ||
                immCheckStatus.get('unSignedTest'),
        };
        const containerClassName = classnames('ra-front-test-position', {
            ['ra-front-test-position--signed-test']:
                testStatus.isValid &&
                !testStatus.isUnsuitable &&
                !testStatus.isNoFinished,
            ['ra-front-test-position--no-test']:
                testStatus.isNoValid && !testStatus.isNoFinished,
            ['ra-front-test-position--unsigned-test']: testStatus.isNoFinished,
        });
        const header = this.buildHeader(testStatus, investProfile);
        const content = this.buildContent(testStatus);
        const linkButton = this.buildLinkButton(testStatus, investProfile);

        return (
            <div className={containerClassName}>
                {header}
                {content}
                <div className="ra-front-test-position__button">
                    {linkButton}
                </div>
            </div>
        );
    }
}

RAFrontTestPosition.propTypes = {
    immCheckStatus: PropTypes.object.isRequired,
    investProfile: PropTypes.string,
    onReturnTestClick: PropTypes.func,
    validateResidential: PropTypes.func.isRequired,
    immFlagBasedFeatures: PropTypes.object.isRequired,
    immRAGlobalPosition: PropTypes.object.isRequired,
};

module.exports = RAFrontTestPosition;
